import { useEffect, useState } from 'react';

import authRequest from '../../../utils/fetch';

import { getBitbucketBaseUrl, getManageBitbucketLink } from './integrations';

export type BitbucketWorkspace = {
	name: string;
	uuid: string;
	slug: string;
};

export type ConnectedBitbucketWorkspacesResponse = {
	cloudId: string;
	workspaces: BitbucketWorkspace[];
};

export type BitbucketConnectedWorkspacesList = {
	workspacesList: BitbucketWorkspace[] | null;
	error: string | null;
	isLoading: boolean;
};

export const BASE_DEV_AGENTS_API_PATH = '/gateway/api/devai-core/v1/dev-agents';
const BB_WORKSPACE_PATH = `${BASE_DEV_AGENTS_API_PATH}/bitbucket/getConnectedWorkspaces`;

export const useBibucketConnectedWorkspacesList = (
	isBitbucketConnected: boolean, // quick solution to ensure we don't make this request if bitbucket is not connected. We should revisit this later.
): BitbucketConnectedWorkspacesList => {
	const [error, setError] = useState<string | null>(null);
	const [workspacesList, setWorkspacesList] = useState<BitbucketWorkspace[] | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const getWorkspacesList = async () => {
		setIsLoading(true);

		const request = authRequest(BB_WORKSPACE_PATH, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		try {
			const res = await fetch(request);

			if (!res.ok) {
				throw new Error(`http error status ${res.status} with text: ${res.statusText}`);
			}

			const response = (await res.json()) as ConnectedBitbucketWorkspacesResponse;
			setWorkspacesList(response.workspaces);
		} catch (e) {
			setError('An error has occured');
		}

		setIsLoading(false);
	};

	useEffect(() => {
		if (isBitbucketConnected) {
			getWorkspacesList();
		}
	}, [isBitbucketConnected]);

	return {
		workspacesList,
		error,
		isLoading,
	};
};

export type WorkspaceItem = {
	name?: string;
	redirectUrl: string;
};

export const constructBbcAutoreviewSettingsUrl = (uuid: string) => {
	const baseUrl = getBitbucketBaseUrl();

	return `${baseUrl}/${uuid}/workspace/settings/rovo-dev-agents`;
};

export const getDefaultBbcWorkspacesPage = () => {
	const baseUrl = getBitbucketBaseUrl();
	return `${baseUrl}/account/workspaces`;
};

export const handleRedirectForWorkspaces = (
	workspacesList: BitbucketWorkspace[] | null,
): WorkspaceItem[] => {
	if (workspacesList === null) {
		return [{ redirectUrl: getDefaultBbcWorkspacesPage() }];
	}

	const totalWorkspaces = workspacesList ? workspacesList.length : 0;

	if (totalWorkspaces === 0) {
		return [{ redirectUrl: getManageBitbucketLink() }];
	}
	return workspacesList.length
		? workspacesList.map((workspace): WorkspaceItem => {
				return {
					redirectUrl: constructBbcAutoreviewSettingsUrl(workspace.uuid),
					name: workspace.name,
				};
			})
		: [];
};

export type InfoPanelDetails = {
	text: string;
	redirectUrl: string;
	linkText: string;
};

export type WorkspacesModalContentType = {
	title: string;
	infoPanelDetails: InfoPanelDetails;
};

export const WorkspacesModalContent: WorkspacesModalContentType = {
	title: 'Choose a workspace to enable Code Reviewer',
	infoPanelDetails: {
		text: 'To enable Code Reviewer on other workspaces, go to workspace settings in Bitbucket and select Rovo Dev Agents. ',
		redirectUrl: getDefaultBbcWorkspacesPage(),
		linkText: 'See all workspaces.',
	},
};
